// src/components/report/FinancialsTab.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LineChart, BarChart, TrendingUp, DollarSign, Clock, Info, Users, Download, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { formatCurrency, getIndustryBenchmark } from '../../utils/reportHelpers';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  'https://ai-workshop-llc-backend.onrender.com';

const _tFallback = (key) => key.split('.').pop();
export const FinancialsTab = ({ financialData, fullReport, industry, subVertical, isIllustrative = false, t = _tFallback, progress = {}, totalTools = 0, onGoToTool = null }) => {
  const peerBusinesses = fullReport?.peerBusinesses || [];
  // ---- ALL HOOKS FIRST ----
  const { reportId } = useParams();
  const benchmark = getIndustryBenchmark(fullReport?.industryBenchmarks, industry, 'roi_benchmark');

  const [benchmarkData, setBenchmarkData] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(true);

  const [roiGoal, setRoiGoal] = useState(() => {
    try { return localStorage.getItem(`roigoal-${reportId}`) || ''; } catch { return ''; }
  });
  const [goalSaved, setGoalSaved] = useState(() => {
    try { return !!localStorage.getItem(`roigoal-${reportId}`); } catch { return false; }
  });
  useEffect(() => {
    if (!industry) { setBenchmarkLoading(false); return; }
    const slug = industry.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    setBenchmarkLoading(true);
    fetch(`${API_BASE}/api/benchmarks/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBenchmarkData(data); })
      .catch(() => {})
      .finally(() => setBenchmarkLoading(false));
  }, [industry]);

  const cumulativeNet = useMemo(() => {
    if (!financialData?.years?.length) return 0;
    return financialData.years.reduce((sum, y) => sum + (y.net || 0), 0);
  }, [financialData?.years]);

  const rois = useMemo(() => {
    if (!financialData?.years?.length) return [];
    return financialData.years.map(y => parseInt(y.roi) || 0);
  }, [financialData?.years]);

  const maxROI = rois.length ? Math.max(...rois) : 100;

  const maxNet = useMemo(() => {
    if (!financialData?.years?.length) return 1;
    return Math.max(...financialData.years.map(y => y.net || 0), 1);
  }, [financialData?.years]);

  const paybackYear = useMemo(() => {
    if (!financialData?.years?.length) return null;
    let running = 0;
    for (const y of financialData.years) {
      running += y.net || 0;
      if (running > 0) return y.year;
    }
    return null;
  }, [financialData?.years]);

  // ---- GUARD AFTER HOOKS ----
  if (!financialData?.years?.length) {
    return (
      <div className="text-center py-16 text-slate-400 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl">
        <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p>Financial projections are being calculated.</p>
        <p className="text-sm mt-2">Complete your audit to unlock your ROI forecast.</p>
      </div>
    );
  }

  // Safe to use financialData now
  const { sensitivity = [], alternatives = [] } = financialData;

  const completedTools = Object.values(progress?.tools || {}).filter(s => s === 'done').length;
  const completionRate = totalTools > 0 ? completedTools / totalTools : 0;
  const hasStarted = completedTools > 0;

  const benchmarkStat = benchmark?.stat
    ? (String(benchmark.stat).includes('%') ? benchmark.stat : `${benchmark.stat}%`)
    : null;

  return (
    <div className="space-y-8">
      {isIllustrative && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-2.5">
          <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400 leading-relaxed">
            All projections are <span className="text-amber-300 font-medium">illustrative estimates</span> based on industry benchmarks — not guarantees. Actual results depend on implementation speed and your specific market.
          </p>
        </div>
      )}
      {/* ── You vs Your Industry benchmark block ── */}
      {fullReport?.industryBenchmark && (() => {
        const bm = fullReport.industryBenchmark;
        const isFirstMover = bm.first_mover_window;
        const positionColor = bm.position === 'ahead' ? 'emerald' : bm.position === 'average' ? 'amber' : 'blue';
        return (
          <div className={`rounded-2xl border ${isFirstMover ? 'border-blue-500/40 bg-blue-500/5' : 'border-white/10 bg-slate-900/50'} p-5`}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">{t('report.benchmark.title')}</p>
                <h4 className={`text-lg font-black text-${positionColor}-400`}>{bm.position_label}</h4>
                {isFirstMover && (
                  <p className="text-xs text-blue-300 mt-1 leading-relaxed">
                    {t('report.benchmark.firstMoverDesc', { pct: bm.not_adopted_pct, industry: bm.industry?.replace(/_/g,' ') })}
                  </p>
                )}
              </div>
              {isFirstMover && (
                <span className="flex-shrink-0 text-[10px] font-bold bg-blue-500/15 text-blue-300 border border-blue-500/25 rounded-full px-2.5 py-1 whitespace-nowrap">
                  {t('report.benchmark.badge')}
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center bg-slate-950/50 rounded-xl p-3">
                <p className="text-2xl font-black text-white">{bm.adopted_pct}%</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-tight" style={{whiteSpace:'pre-line'}}>{t('report.benchmark.adoptedLabel')}</p>
              </div>
              <div className="text-center bg-slate-950/50 rounded-xl p-3">
                <p className="text-2xl font-black text-white">{bm.not_adopted_pct}%</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-tight" style={{whiteSpace:'pre-line'}}>{t('report.benchmark.notAdoptedLabel')}</p>
              </div>
              <div className="text-center bg-slate-950/50 rounded-xl p-3">
                <p className="text-2xl font-black text-[#3b82f6]">+{bm.best_lift_pct}%</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-tight" style={{whiteSpace:'pre-line'}}>{t('report.benchmark.liftLabel')}</p>
              </div>
            </div>
            {bm.best_tool_name && bm.best_sample_size > 0 && (() => {
              // Round-11: Sarah caught raw "2000000" rendering as a
              // template-variable-looking number. Format compactly: 2M
              // for >=1M, 250K for >=10K, otherwise comma-separated.
              const n = Number(bm.best_sample_size) || 0;
              const display =
                n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1).replace(/\.0$/, '')}M`
                : n >= 10_000 ? `${(n / 1_000).toFixed(0)}K`
                : n.toLocaleString();
              return (
                <p className="text-[11px] text-slate-400 border-t border-white/8 pt-3 leading-relaxed">
                  {t('report.benchmark.sampleNote', { count: display, toolName: bm.best_tool_name, pct: bm.best_lift_pct })}
                </p>
              );
            })()}
          </div>
        );
      })()}

      {/* Header summary card with payback period */}
      <Card className="bg-slate-900/70 backdrop-blur-xl border border-blue-500/25">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#3b82f6]" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total 3‑Year Net Benefit</p>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(cumulativeNet)}
                </p>
              </div>
            </div>
            {paybackYear && (
              <div className="text-center">
                <p className="text-sm text-slate-400">Payback Period</p>
                <p className="text-2xl font-bold text-[#3b82f6]">Year {paybackYear}</p>
              </div>
            )}
            {benchmarkStat && (
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 px-3 py-1">
                {industry} avg ROI: {benchmarkStat}
              </Badge>
            )}
          </div>

          {/* Concrete ROI breakdown */}
          {financialData?.years?.[0] && (() => {
            const yr1Net = financialData.years[0].net || 0;
            const mo = Math.round(yr1Net / 12);
            const oneTime  = financialData?.oneTimeRecovery || 0;
            const recurring = financialData?.recurringAnnual || 0;
            // Round-17 (Sarah dental verdict): industry-aware framing
            // template. The R14 fix only had a binary "is service biz"
            // toggle that rendered "X extra client payments at $1,200
            // each" for everyone in {legal, accounting, dental,
            // healthcare, real_estate, ...}. Sarah caught this on
            // dental: "client payments" reads wrong for a dentist
            // (it's "patient procedures") and the $1,200 average is
            // plausible for a single procedure but not the right anchor
            // for every vertical.
            //
            // Each industry now has its own (unit, unitPrice) pair. If
            // an industry isn't listed, we hide the unit-framing
            // sentence entirely and just show the $/year — honest
            // fallback that doesn't fabricate a unit price the buyer
            // could check against.
            const INDUSTRY_FRAMING = {
              dental:                  { unit: "patient procedures",        unitPrice: 800 },
              healthcare:              { unit: "patient visits",            unitPrice: 250 },
              legal:                   { unit: "billable hours",            unitPrice: 350 },
              accounting:              { unit: "client engagements",        unitPrice: 1500 },
              real_estate:             { unit: "extra deal commissions",    unitPrice: 6000 },
              "real-estate":           { unit: "extra deal commissions",    unitPrice: 6000 },
              // R31.7c (Sarah R34d verdict): PM sub-vertical framing.
              // Cedar Ridge persona (PM with AppFolio + Buildium +
              // tenant_screening_01 chip) was getting "32 extra deal
              // commissions at $6,000.00 each" — pure brokerage copy
              // for a buyer who manages doors, doesn't sell them. The
              // sub_vertical detection below routes PM personas to
              // this framing instead.
              property_management:     { unit: "tenants retained per year",  unitPrice: 1800 },
              "property-management":   { unit: "tenants retained per year",  unitPrice: 1800 },
              hvac:                    { unit: "extra service calls",       unitPrice: 350 },
              home_services:           { unit: "extra service calls",       unitPrice: 350 },
              auto_repair:             { unit: "extra service tickets",     unitPrice: 500 },
              "auto-repair":           { unit: "extra service tickets",     unitPrice: 500 },
              construction:            { unit: "extra project bids won",    unitPrice: 8000 },
              hospitality:             { unit: "extra covers",              unitPrice: 65 },
              restaurant:              { unit: "extra covers",              unitPrice: 65 },
              med_spa:                 { unit: "extra appointments",        unitPrice: 350 },
              "med-spa":               { unit: "extra appointments",        unitPrice: 350 },
              professional_services:   { unit: "billable hours",            unitPrice: 200 },
              // Explicit null = hide unit framing entirely. ARR/MRR and
              // basket-size businesses don't map cleanly to a single
              // "X units at $Y each" anchor. Just show the $/year.
              ecommerce:               null,
              retail:                  null,
              shopify:                 null,
              saas:                    null,
              technology:              null,
              education:               null,
              finance:                 null,
              manufacturing:           null,
            };
            // R31.7c (Sarah R34d verdict): PM sub-vertical detection.
            // When industry=real_estate AND any matched chip signals PM
            // (tenant_screening_01, lease_renewal_01, maintenance_chaos_01,
            // tenant_screening, re_client_retention_01), prefer the
            // property_management framing over the brokerage one. This
            // is the same defect class as the audit-form / cluster-
            // taxonomy bleed Sarah called out across R32-R34: industry
            // alone isn't enough to disambiguate brokerage vs PM.
            const _industryNorm = (industry || "").toLowerCase();
            const _matchedProblems = (
              fullReport?.ownerProblems?.matched_problems ||
              fullReport?.matchedProblems ||
              []
            );
            const _PM_CHIP_IDS = new Set([
              "tenant_screening_01", "lease_renewal_01",
              "maintenance_chaos_01", "re_client_retention_01",
              "re_vendor_coordination_01",
            ]);
            const _hasPmChip = _matchedProblems.some((p) => {
              const pid = typeof p === "string" ? p : (p && p.id) || "";
              return _PM_CHIP_IDS.has(pid);
            });
            const _frameKey = (_industryNorm === "real_estate" && _hasPmChip)
              ? "property_management"
              : _industryNorm;
            const _frame = INDUSTRY_FRAMING[_frameKey] || null;
            const unitCount = (_frame && yr1Net > 0)
              ? Math.round(yr1Net / _frame.unitPrice) : null;
            return yr1Net > 0 ? (
              <div className="mt-4 pt-4 border-t border-white/8 space-y-2">
                <p className="text-xs text-slate-400">
                  <span className="text-white font-semibold">{formatCurrency(yr1Net)}/year</span>
                  {unitCount && unitCount > 1 && _frame && (
                    <span> — that's roughly <span className="text-[#3b82f6] font-medium">{unitCount} {_frame.unit} at {formatCurrency(_frame.unitPrice)} each</span> recovered over the year</span>
                  )}
                </p>
                {oneTime > 0 && recurring > 0 && (() => {
                  // Round-11: Sarah caught a math mismatch. The previous
                  // copy said "Y1 includes ~$X one-time + Years 2–3 at
                  // ~$Y/year recurring" — implying gross sum of
                  // $X + $Y + $Y. But the cumulative number above is NET
                  // (returns minus year-1 setup + tool subscriptions).
                  // Buyer can't reconcile $7,200 + $25,824 + $25,824 =
                  // $58,848 with the displayed $47,380. Fix: show the
                  // actual net value per year so the breakdown sums
                  // exactly to the cumulative shown above.
                  const yrs = financialData?.years || [];
                  const y1Net = yrs[0]?.net || 0;
                  const y23NetAvg = yrs.length >= 3
                    ? Math.round(((yrs[1]?.net || 0) + (yrs[2]?.net || 0)) / 2)
                    : 0;
                  return (
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Year 1 net: <span className="text-slate-300 font-medium">{formatCurrency(y1Net)}</span> (gross ~{formatCurrency(oneTime)} backlog + {formatCurrency(recurring)} recurring, minus year-1 setup &amp; tool costs).
                      Years 2–3 average ~<span className="text-slate-300 font-medium">{formatCurrency(y23NetAvg)}/year</span> net (recurring minus tool subscriptions).
                      These sum to the {formatCurrency(cumulativeNet)} cumulative above.
                    </p>
                  );
                })()}
                <p className="text-xs text-slate-500">
                  <span className="text-slate-400 font-medium">What this could fund:</span> a part-time admin hire, a full marketing campaign, or {Math.round(mo / 500)} months of your own salary buffer (≈ {formatCurrency(mo)}/mo)
                </p>
                <p className="text-[10px] text-slate-600 italic">If you don't feel this blueprint is worth at least 10× what you paid, we'll refund you — no questions asked.</p>
              </div>
            ) : null;
          })()}
        </CardContent>
      </Card>

      {/* ── How We Calculated This — assumption transparency ── */}
      {(() => {
        const assumptions = fullReport?.roiProjections?.assumptions;
        if (!assumptions) return null;
        const {
          monthlyRevenueAssumed,
          monthlyRevenueSource,
          hourlyRateAssumed,
          hourlyRateSource,
          adoptionRatePct,
          rampUpWeeks,
          riskAdjustmentPct,
          industryAvgLiftPct,
          automationSavingsPotentialPct,
          toolCostBreakdown = [],
          timeSavingsBreakdown = [],
          implementationOpportunityCostUSD,
          founderHoursAssumed,
          sensitivityScenarios = {},
          notes,
        } = assumptions;

        const fmt = (v) => (typeof v === 'number' ? formatCurrency(v) : v || '—');

        return (
          <details className="group rounded-2xl border border-blue-500/20 bg-slate-900/50 backdrop-blur-xl overflow-hidden">
            <summary className="cursor-pointer list-none p-5 flex items-center justify-between hover:bg-white/5 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center">
                  <Info className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">How we calculated this</p>
                  <p className="text-xs text-slate-400">Every ROI number, traced back to the assumption behind it.</p>
                </div>
              </div>
              <span className="text-xs text-[#3b82f6] font-medium group-open:hidden">Expand</span>
              <span className="text-xs text-[#3b82f6] font-medium hidden group-open:inline">Collapse</span>
            </summary>

            <div className="px-5 pb-6 pt-2 space-y-6 border-t border-white/8">
              {/* Headline assumptions */}
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div className="rounded-lg border border-white/8 bg-slate-900/60 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Monthly revenue baseline</p>
                  <p className="text-lg font-bold text-white">
                    {monthlyRevenueAssumed ? fmt(monthlyRevenueAssumed) : 'Industry proxy'}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">Source: {monthlyRevenueSource || 'industry proxy'}</p>
                </div>
                <div className="rounded-lg border border-white/8 bg-slate-900/60 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Hourly rate assumed</p>
                  <p className="text-lg font-bold text-white">${hourlyRateAssumed}/hr</p>
                  <p className="text-[11px] text-slate-500 mt-1">{hourlyRateSource || 'BLS labor benchmark'}</p>
                </div>
                <div className="rounded-lg border border-white/8 bg-slate-900/60 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Adoption rate assumed</p>
                  <p className="text-lg font-bold text-white">{adoptionRatePct}%</p>
                  <p className="text-[11px] text-slate-500 mt-1">How aggressively your team actually uses each tool</p>
                </div>
                <div className="rounded-lg border border-white/8 bg-slate-900/60 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Ramp-up period</p>
                  <p className="text-lg font-bold text-white">{rampUpWeeks} weeks</p>
                  <p className="text-[11px] text-slate-500 mt-1">Time until full benefit is realized</p>
                </div>
                <div className="rounded-lg border border-white/8 bg-slate-900/60 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Risk adjustment applied</p>
                  <p className="text-lg font-bold text-white">−{riskAdjustmentPct}%</p>
                  <p className="text-[11px] text-slate-500 mt-1">Discount applied for implementation friction</p>
                </div>
                <div className="rounded-lg border border-white/8 bg-slate-900/60 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Industry avg AI lift</p>
                  <p className="text-lg font-bold text-white">{industryAvgLiftPct}%</p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Automation savings potential: {automationSavingsPotentialPct}%
                  </p>
                </div>
              </div>

              {/* Tool cost breakdown */}
              {toolCostBreakdown.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Tool cost breakdown
                  </h4>
                  <div className="overflow-x-auto rounded-lg border border-white/8">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Tool</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium">Monthly cost</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium">Annual cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {toolCostBreakdown.map((row) => (
                          <tr key={row.tool} className="border-t border-white/8">
                            <td className="py-2 px-3 text-slate-200">{row.tool}</td>
                            <td className="py-2 px-3 text-right text-slate-300">${row.monthlyCost.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right text-[#3b82f6]">${row.annualCost.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Time savings breakdown */}
              {timeSavingsBreakdown.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Time savings breakdown
                  </h4>
                  <div className="overflow-x-auto rounded-lg border border-white/8">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Source of savings</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium">Hrs / week</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium">Annual hrs</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium">Annual value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timeSavingsBreakdown.map((row) => (
                          <tr key={row.source} className="border-t border-white/8">
                            <td className="py-2 px-3 text-slate-200">{row.source}</td>
                            <td className="py-2 px-3 text-right text-slate-300">{row.hoursPerWeek}</td>
                            <td className="py-2 px-3 text-right text-slate-300">{row.annualHours}</td>
                            <td className="py-2 px-3 text-right text-[#3b82f6]">${(row.annualValue || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Implementation opportunity cost */}
              {implementationOpportunityCostUSD ? (
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-400 mb-1">
                    Implementation opportunity cost
                  </p>
                  <p className="text-sm text-slate-300">
                    ≈ <span className="text-white font-semibold">${implementationOpportunityCostUSD.toLocaleString()}</span>
                    {' '}of founder/team time over the 90-day rollout
                    {founderHoursAssumed ? ` (${founderHoursAssumed} hrs @ $${hourlyRateAssumed}/hr)` : ''}.
                    This is built into the risk adjustment above.
                  </p>
                </div>
              ) : null}

              {/* Sensitivity scenarios */}
              {(sensitivityScenarios.conservative || sensitivityScenarios.expected || sensitivityScenarios.optimistic) && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Sensitivity scenarios
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {['conservative', 'expected', 'optimistic'].map((k) => {
                      const row = sensitivityScenarios[k];
                      if (!row) return null;
                      const label = k === 'expected' ? 'Expected' : k.charAt(0).toUpperCase() + k.slice(1);
                      const positive = (row.year1Net || 0) >= 0;
                      return (
                        <div
                          key={k}
                          className={`rounded-lg border p-3 text-center ${
                            k === 'expected' ? 'border-blue-500/30 bg-blue-500/5' : 'border-white/8 bg-slate-900/60'
                          }`}
                        >
                          <p className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</p>
                          <p className="text-xs text-slate-400 mt-1">{row.adoptionPct}% adoption</p>
                          <p className={`text-xl font-bold mt-1 ${positive ? 'text-[#3b82f6]' : 'text-amber-400'}`}>
                            {positive ? '' : '−'}${Math.abs(row.year1Net).toLocaleString()}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Year 1 net</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {notes && (
                <p className="text-[11px] text-slate-500 italic leading-relaxed border-t border-white/8 pt-3">
                  {notes}
                </p>
              )}
            </div>
          </details>
        );
      })()}

      {/* Commit to your ROI */}
      <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-5">
        <div className="flex items-start gap-3 mb-3">
          <CheckCircle2 className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-white">Commit to your ROI</p>
            <p className="text-xs text-slate-400 mt-0.5">Set a personal target and track it weekly.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={roiGoal}
            onChange={e => { setRoiGoal(e.target.value); setGoalSaved(false); }}
            placeholder={`e.g. "Implement 3 tools by ${new Date(Date.now() + 90 * 864e5).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}"`}
            className="flex-1 bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/40"
          />
          <button
            onClick={() => {
              if (!roiGoal.trim()) return;
              try { localStorage.setItem(`roigoal-${reportId}`, roiGoal.trim()); } catch {}
              setGoalSaved(true);
            }}
            className="flex-shrink-0 px-4 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-[#3b82f6] text-sm font-medium transition-colors"
          >
            {goalSaved ? '✓ Saved' : 'Save'}
          </button>
        </div>
        {goalSaved && roiGoal && (
          <p className="text-xs text-[#3b82f6] mt-2">Your goal: "{roiGoal}" — we'll remind you to check in weekly.</p>
        )}
      </div>

      {/* 3‑Year Financial Projection with progress bars */}
      <div>
        <h3 className="text-xl font-semibold text-[#3b82f6] mb-4 flex items-center gap-2">
          <LineChart className="h-5 w-5" /> 3‑Year Financial Projection
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Year</th>
                <th className="text-right py-3 px-4 text-slate-400 font-medium">Investment</th>
                <th className="text-right py-3 px-4 text-slate-400 font-medium">Returns</th>
                <th className="text-right py-3 px-4 text-slate-400 font-medium">Net Benefit</th>
                <th className="text-right py-3 px-4 text-slate-400 font-medium">ROI</th>
              </tr>
            </thead>
            <tbody>
              {financialData.years.map((row) => {
                const roiNum = parseInt(row.roi) || 0;
                const roiPercent = maxROI ? (roiNum / maxROI) * 100 : 0;
                return (
                  <tr key={row.year} className="border-b border-white/8 hover:bg-white/4">
                    <td className="py-3 px-4 text-white font-medium">Year {row.year}</td>
                    <td className="py-3 px-4 text-right text-slate-300">{formatCurrency(row.investment)}</td>
                    <td className="py-3 px-4 text-right text-[#3b82f6]">{formatCurrency(row.returns)}</td>
                    <td className="py-3 px-4 text-right text-[#3b82f6]">{formatCurrency(row.net)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-bold text-[#3b82f6]">{row.roi}</span>
                        <div className="w-16 bg-white/10 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full"
                            style={{ width: `${roiPercent}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {hasStarted && (
                <tr className="border-b border-white/8 bg-blue-500/5">
                  <td className="py-3 px-4 text-blue-300 font-medium text-xs" colSpan={2}>
                    At your current pace ({completedTools}/{totalTools} tools implemented)
                  </td>
                  {financialData.years.map(row => (
                    <td key={row.year} className="py-3 px-4 text-right text-blue-300 text-xs">
                      {formatCurrency(Math.round((row.net || 0) * completionRate))}
                    </td>
                  ))}
                  <td className="py-3 px-4 text-right text-blue-300 text-xs font-bold">
                    {Math.round(completionRate * 100)}%
                  </td>
                </tr>
              )}
              {!hasStarted && (
                <tr className="border-t border-white/8">
                  <td colSpan={5} className="py-3 px-4">
                    <p className="text-xs text-slate-500 text-center">
                      Start implementing tools to see your actual vs. projected returns update here.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {benchmark && (
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
            <BarChart className="inline w-3 h-3" />
            Based on {benchmark.sampleSize} {industry} businesses in our database — avg. {benchmark.stat} ROI in {benchmark.timeframe}.
          </p>
        )}
      </div>

      {/* Sensitivity Analysis with tooltips */}
      {sensitivity.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-blue-400 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" /> Sensitivity Analysis
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sensitivity.map((row) => (
              <TooltipProvider key={row.name}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 hover:border-blue-500/30 transition-all cursor-help">
                      <CardContent className="p-4 text-center">
                        <div className="text-sm text-slate-400 mb-1">{row.name}</div>
                        <div className="text-2xl font-bold text-blue-400">{row.roi}</div>
                        <div className="text-xs text-slate-500 mt-1">{row.effectiveness} effectiveness</div>
                      </CardContent>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-slate-950/50 border-white/8 text-slate-300 max-w-xs">
                    <p className="text-xs">Assumes {row.effectiveness} of projected efficiency gains. Your actual ROI may vary based on implementation speed and tool adoption.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      )}

      {/* Cumulative Net Benefit Sparkline */}
      <div>
        <h3 className="text-xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5" /> Cumulative Net Benefit
        </h3>
        <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-2xl font-bold text-white">{formatCurrency(cumulativeNet)}</div>
              <Badge className="bg-blue-500/20 text-[#3b82f6]">over 3 years</Badge>
            </div>
            <div className="flex items-end gap-1 h-16">
              {financialData.years.map((row, idx) => {
                const height = Math.max(4, ((row.net || 0) / maxNet) * 100);
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-blue-500/20 rounded-t-lg"
                      style={{ height: `${height}%` }}
                    />
                    <span className="text-xs text-slate-500 mt-1">Y{row.year}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Investment Alternatives Comparison */}
      {alternatives.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
            <BarChart className="h-5 w-5" /> Investment Alternatives Comparison
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/8">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">Option</th>
                  <th className="text-right py-3 px-4 text-slate-400 font-medium">Cost</th>
                  <th className="text-right py-3 px-4 text-slate-400 font-medium">ROI</th>
                  <th className="text-right py-3 px-4 text-slate-400 font-medium">Timeline</th>
                </tr>
              </thead>
              <tbody>
                {alternatives.map((row) => {
                  const isThisPlan = row.option.includes('This Plan');
                  return (
                    <tr key={row.option} className={`border-b border-white/8 ${isThisPlan ? 'bg-blue-900/20' : 'hover:bg-white/4'}`}>
                      <td className={`py-3 px-4 font-medium ${isThisPlan ? 'text-[#3b82f6]' : 'text-white'}`}>
                        {row.option} {isThisPlan && '⭐'}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-300">{row.cost}</td>
                      <td className={`py-3 px-4 text-right font-bold ${isThisPlan ? 'text-[#3b82f6]' : 'text-slate-300'}`}>{row.roi}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{row.timeline}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
            <Info className="inline w-3 h-3" />
            Alternatives based on typical SMB investment options in your industry.
          </p>
        </div>
      )}

      {/* How You Compare — Industry Benchmark Panel */}
      {(benchmarkLoading || benchmarkData) && (
        <div>
          <h3 className="text-xl font-semibold text-amber-400 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5" /> How You Compare
          </h3>
          {benchmarkLoading ? (
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
              <CardContent className="p-6 space-y-4 animate-pulse">
                <div className="flex justify-between items-center mb-2">
                  <div className="h-4 bg-white/10 rounded w-40" />
                  <div className="h-6 bg-white/10 rounded w-12" />
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5" />
                <div className="h-16 bg-white/8 border border-white/8 rounded-lg" />
                <div className="h-4 bg-white/10 rounded w-3/4" />
              </CardContent>
            </Card>
          ) : benchmarkData ? (
          <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
            <CardContent className="p-6 space-y-5">
              {/* Adoption rate gauge */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-400">Industry AI adoption rate</span>
                  <span className="text-lg font-bold text-amber-400">{benchmarkData.adoptionRate}%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5">
                  <div
                    className="bg-amber-500 h-2.5 rounded-full"
                    style={{ width: `${Math.min(benchmarkData.adoptionRate, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Average payback: <span className="text-slate-300">{benchmarkData.avgRoiMonths} months</span>
                </p>
              </div>

              {/* Top metric callout */}
              {benchmarkData.topMetric && (
                <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
                  <p className="text-sm text-amber-200 leading-relaxed">"{benchmarkData.topMetric}"</p>
                  <p className="text-xs text-amber-500/70 mt-2">— {benchmarkData.source}</p>
                </div>
              )}

              {/* Peer comparison */}
              <p className="text-sm text-slate-300 leading-relaxed">{benchmarkData.peerComparison}</p>

              {/* Real user outcomes (only shown when sample size > 0) */}
              {benchmarkData.userOutcomes?.sampleSize > 0 && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/8">
                  {benchmarkData.userOutcomes.medianHoursSaved != null && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#3b82f6]">{benchmarkData.userOutcomes.medianHoursSaved}h</p>
                      <p className="text-xs text-slate-400 mt-1">median hours saved/week</p>
                    </div>
                  )}
                  {benchmarkData.userOutcomes.medianRevenueLift != null && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#3b82f6]">{benchmarkData.userOutcomes.medianRevenueLift}%</p>
                      <p className="text-xs text-slate-400 mt-1">median revenue lift</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          ) : null}
        </div>
      )}

      {/* Semantically Similar Businesses — AI Workshop Implementation Engine™ */}
      {peerBusinesses.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-amber-400 mb-1 flex items-center gap-2">
            <Users className="h-5 w-5" /> Businesses Like Yours Chose
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Matched by AI Workshop Implementation Engine™ — semantic similarity to your business profile
          </p>
          <div className="space-y-3">
            {peerBusinesses.map((peer, i) => (
              <Card key={i} className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-200 truncate">{peer.businessName}</p>
                      <p className="text-xs text-slate-500 mb-2">{peer.industry}</p>
                      <p className="text-xs text-slate-400 leading-relaxed">{peer.notes}</p>
                    </div>
                    <Badge className="bg-amber-900/40 text-amber-300 border-amber-700/40 text-xs shrink-0">
                      {Math.round((peer.similarity || 0) * 100)}% match
                    </Badge>
                  </div>
                  {peer.topTools?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/8">
                      <p className="text-xs text-slate-500 mb-2">Tools they implemented:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {peer.topTools.slice(0, 4).map((tool, j) => (
                          onGoToTool ? (
                            <button
                              key={j}
                              onClick={() => onGoToTool(tool)}
                              className="text-xs bg-slate-900/60 hover:bg-blue-500/10 border border-white/8 hover:border-blue-500/30 text-slate-300 hover:text-[#3b82f6] px-2 py-0.5 rounded-full transition-colors"
                            >
                              {tool} →
                            </button>
                          ) : (
                            <span key={j} className="text-xs bg-slate-900/60 border border-white/8 text-slate-300 px-2 py-0.5 rounded-full">
                              {tool}
                            </span>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── What to measure weekly ──
          R45 (Maya/Sarah verdict): the middle KPI card was hardcoded
          "Lead/customer response time · Under 2 hours for new leads" —
          a brokerage-funnel KPI that surfaced on every vertical including
          PM. Wire the three cards to industry-aware values: the first
          card (hours saved) is universal, the third (Y1 ROI) is dataset-
          driven; the middle now comes from a per-sub-vertical or per-
          industry KPI table. Falls through to the generic "Lead response
          time" only for verticals that genuinely sell to leads. */}
      {(() => {
        const _industryMetrics = fullReport?.industryContext?.metrics || {};
        // R46 (Maya R2 verdict): R43 humanized the API-serialized
        // industry field ("Property Management", "E-commerce") so the
        // frontend receives a display label not a slug. The R45 KPI
        // lookup was keyed on slug ("ecommerce", "property_management")
        // and missed every vertical — every paid report rendered the
        // default "Customer/lead response time" middle KPI card.
        // Fix: flatten both the lookup key AND map keys to a single
        // hyphen/space/underscore-stripped lowercase token so
        // "E-commerce" / "e-commerce" / "ecommerce" all match.
        const _flatten = (s) => (s || "").toLowerCase().replace(/[\s_\-&]+/g, "").trim();
        const _svKey = _flatten(subVertical);
        const _indKey = _flatten(industry);

        // Per-(sub_)vertical KPI #2 override. Pulls the most-actionable
        // weekly metric a buyer in that vertical actually tracks. Keys
        // are flattened form ("ecommerce" matches "E-commerce" /
        // "e-commerce" / "Ecommerce" / "e_commerce").
        const _MIDDLE_KPI_BY_VERTICAL = {
          propertymanagement: {
            metric: 'Vacancy days per turnover',
            source: 'PMS dashboard (AppFolio, Buildium, DoorLoop) or unit-status log',
            good: 'Under 14 days per turnover by week 8',
          },
          ecommerce: {
            metric: 'Cart abandonment recovery rate',
            source: 'Klaviyo, Omnisend, or your ESP\'s abandoned-cart report',
            good: '12%+ recovery on abandoned carts by week 8',
          },
          retail: {
            metric: 'Cart abandonment recovery rate',
            source: 'Klaviyo, Omnisend, or your ESP\'s abandoned-cart report',
            good: '12%+ recovery on abandoned carts by week 8',
          },
          dental: {
            metric: 'Patient no-show rate',
            source: 'PMS (Dentrix, Eaglesoft) or scheduling dashboard',
            good: 'Under 8% no-show rate by week 8',
          },
          healthcare: {
            metric: 'Patient no-show rate',
            source: 'EHR scheduling dashboard',
            good: 'Under 8% no-show rate by week 8',
          },
          hvac: {
            metric: 'Missed-call recovery rate',
            source: 'Call-tracking dashboard (CallRail, your phone system)',
            good: '90%+ of missed calls returned within 2 hours',
          },
          homeservices: {
            metric: 'Missed-call recovery rate',
            source: 'Call-tracking dashboard or your AI receptionist log',
            good: '90%+ of missed calls returned within 2 hours',
          },
          legal: {
            metric: 'Intake-to-engagement time',
            source: 'Practice management system (Clio, MyCase) intake log',
            good: 'Under 24 hours from first contact to signed engagement letter',
          },
          accounting: {
            metric: 'Document collection cycle time',
            source: 'Document portal (SmartVault, ShareFile) or client tracker',
            good: 'Under 5 business days from request to upload',
          },
          restaurant: {
            metric: 'Reservation no-show rate',
            source: 'OpenTable, Resy, or your reservation system dashboard',
            good: 'Under 5% no-show rate by week 8',
          },
          hospitality: {
            metric: 'Reservation no-show rate',
            source: 'PMS or reservation dashboard',
            good: 'Under 5% no-show rate by week 8',
          },
          construction: {
            metric: 'Quote-to-signed-contract time',
            source: 'CRM or estimating software pipeline',
            good: 'Under 7 days from site visit to signed contract',
          },
        };
        const _middleKpi =
          _MIDDLE_KPI_BY_VERTICAL[_svKey] ||
          _MIDDLE_KPI_BY_VERTICAL[_indKey] ||
          {
            metric: 'Customer/lead response time',
            source: 'CRM, email analytics, or inbox reply-time report',
            good: 'Under 2 hours for new inquiries',
          };

        const kpis = [
          {
            metric: 'Hours saved on manual tasks',
            source: 'Time-tracking app (Toggl, Clockify) or a simple weekly log',
            good: '3+ hrs/week by week 4',
            color: 'emerald',
          },
          {
            ..._middleKpi,
            color: 'blue',
          },
          {
            metric: 'Revenue from AI-assisted workflows',
            source: 'Stripe, Shopify, or CRM pipeline — tag AI-influenced deals',
            good: `${financialData.years[0]?.roi || '150%'}+ ROI by end of Year 1`,
            color: 'purple',
          },
        ];

        const downloadKPITracker = () => {
          const y = financialData.years;
          const rows = [
            ['AI Implementation KPI Tracker — AI Workshop LLC'],
            [''],
            ['FINANCIAL TARGETS'],
            ['Year', 'Investment ($)', 'Target Returns ($)', 'Net Benefit ($)', 'ROI Target'],
            ...(y.map(row => [`Year ${row.year}`, row.investment, row.returns, row.net, row.roi])),
            [''],
            ['WEEKLY METRICS TO TRACK'],
            ['Metric', 'Baseline (Week 0)', 'Week 4 Target', 'Week 8 Target', 'Week 12 Target', 'Where to track'],
            ['Hours saved on manual tasks (hrs/week)', '', '3+', '5+', '8+', 'Time tracker or manual log'],
            // R45: middle row mirrors the on-screen middle KPI card so the
            // CSV download tracks the same vertical-correct metric.
            [_middleKpi.metric, '', '', _middleKpi.good, '', _middleKpi.source],
            ['AI tool setup completion (%)', '0%', '25%', '60%', '100%', 'Report progress tracker'],
            ['Revenue from AI-assisted workflows ($)', '', '', '', '', 'CRM / Stripe dashboard'],
            [''],
            ['NOTES'],
            ['Fill in your Baseline (Week 0) numbers BEFORE you start implementing.'],
            ['Review this tracker every Friday. Flag any metric where "good" is not reached by Week 8.'],
            [''],
            ['Generated by AI Workshop LLC — aiworkshopllc.com'],
          ];
          const csv = rows
            .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `AI_KPI_Tracker_${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        };

        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#3b82f6] flex items-center gap-2">
                <TrendingUp className="h-5 w-5" /> What to Measure Weekly
              </h3>
              <button
                onClick={downloadKPITracker}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/25 text-[#3b82f6] transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Download KPI Tracker (CSV)
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {kpis.map(({ metric, source, good, color }) => (
                <div key={metric} className={`rounded-xl border p-4 ${
                  color === 'emerald' ? 'bg-blue-500/5 border-blue-500/20' :
                  color === 'blue'    ? 'bg-blue-500/5 border-blue-500/20' :
                                       'bg-purple-500/5 border-purple-500/20'
                }`}>
                  <p className={`text-sm font-semibold mb-3 ${
                    color === 'emerald' ? 'text-[#3b82f6]' :
                    color === 'blue'    ? 'text-blue-400' : 'text-purple-400'
                  }`}>{metric}</p>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start gap-2">
                      <Clock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-slate-500 block">Where to track</span>
                        <span className="text-slate-300">{source}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-slate-500 block">What "good" looks like</span>
                        <span className="text-slate-300 font-medium">{good}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              💡 Track these every Friday. If you're not hitting "good" by Week 8, revisit which tool to replace first.
            </p>
          </div>
        );
      })()}
    </div>
  );
};